import type { VaultSidebarMenuItem } from './vault-sidebar-menu.types';

/** Iconos Lucide registrados en vault-shell (app.config LUCIDE_ICONS). */
export const VAULT_MENU_ICON_BY_OPTION_KEY: Record<string, string> = {
  SUB_MODULES_VAULT_HOME_NAME: 'House',
  OPTIONS_VAULT_DASHBOARD: 'Gauge',
  SUB_MODULES_VAULT_DOCU_NAME: 'Folder',
  SUB_MODULES_VAULT_DOCUMENTAL_NAME: 'Folder',
  OPTIONS_VAULT_INGEST: 'FolderUp',
  OPTIONS_VAULT_PRESERVATION: 'FolderLock',
  OPTIONS_VAULT_RETENTION: 'FolderGit2',
  OPTIONS_VAULT_LEGAL_HOLD: 'FolderArchive',
  OPTIONS_VAULT_REPOSITORIO_ADMIN: 'Vault',
  OPTIONS_VAULT_REPORTS: 'BarChart3',
  OPTIONS_VAULT_CLOUD: 'FolderSync',
  SUB_MODULES_VAULT_SYSTEM_NAME: 'BetweenHorizontalEnd',
  OPTIONS_VAULT_AUDIT: 'SearchCheck',
  OPTIONS_VAULT_SETTINGS: 'Settings',
  OPTIONS_VAULT_PRESERVATION_MESSAGES: 'MessageSquareDot',
  OPTIONS_VAULT_NOTIFICATIONS: 'MessageSquareDot',
  SUB_MODULES_VAULT_ADMIN_NAME: 'UserStar',
  OPTIONS_VAULT_ADMIN_TENANTS: 'Building2',
  OPTIONS_VAULT_ADMIN_USERS: 'Users',
  OPTIONS_VAULT_ADMIN_USER: 'Users',
  OPTIONS_VAULT_ADMIN_MODULE_PERMISSIONS: 'Key',
};

/** Etiquetas en español (UI / foto referencia). */
export const VAULT_MENU_ICON_BY_LABEL: Record<string, string> = {
  'mi tablero': 'House',
  inicio: 'Gauge',
  'centro documental': 'Folder',
  'carga de documentos': 'FolderUp',
  'preservacion digital': 'FolderLock',
  'retencion documental': 'FolderGit2',
  'custodia legal': 'FolderArchive',
  'boveda digital': 'Vault',
  'reportes generales': 'BarChart3',
  'respaldo externo': 'FolderSync',
  'centro de control': 'BetweenHorizontalEnd',
  auditoria: 'SearchCheck',
  'historial de actividad': 'SearchCheck',
  configuracion: 'Settings',
  'configuracion general': 'Settings',
  'mensajes informativos': 'MessageSquareDot',
  notificaciones: 'MessageSquareDot',
  administrador: 'UserStar',
  'gestion de empresas': 'Building2',
  'gestion de usuarios': 'Users',
  permisos: 'Key',
};

function normalizeLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function toLucidePascalCase(segment: string): string {
  return segment
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join('');
}

/** Convierte valores del API (lucide-*, fa-*, PascalCase) a nombre Lucide. */
export function normalizeApiIconToken(raw?: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;

  if (/^[A-Z][a-zA-Z0-9]*$/.test(value)) return value;

  const lucideMatch = value.match(/lucide[_-]?([a-z0-9-]+)/i);
  if (lucideMatch?.[1]) return toLucidePascalCase(lucideMatch[1]);

  const faMap: Record<string, string> = {
    'fa-home': 'House',
    'fa-folder': 'Folder',
    'fa-folder-open': 'Folder',
    'fa-upload': 'FolderUp',
    'fa-lock': 'FolderLock',
    'fa-cloud': 'Cloud',
    'fa-bar-chart': 'BarChart3',
    'fa-cog': 'Settings',
    'fa-gear': 'Settings',
    'fa-building': 'Building2',
    'fa-users': 'Users',
    'fa-user': 'Users',
    'fa-star': 'UserStar',
    'fa-shield': 'Shield',
    'fa-archive': 'ArchiveRestore',
    'fa-tachometer': 'Gauge',
    'fa-gauge': 'Gauge',
    'fa-check-circle': 'CircleCheck',
    'fa-comment': 'MessageSquareDot',
  };

  const lower = value.toLowerCase();
  for (const [fa, icon] of Object.entries(faMap)) {
    if (lower.includes(fa)) return icon;
  }

  return null;
}

export function resolveVaultMenuIcon(
  iconFromApi?: string,
  label?: string,
  optionKey?: string,
): string {
  if (optionKey && VAULT_MENU_ICON_BY_OPTION_KEY[optionKey]) {
    return VAULT_MENU_ICON_BY_OPTION_KEY[optionKey];
  }

  if (label) {
    const byLabel = VAULT_MENU_ICON_BY_LABEL[normalizeLabel(label)];
    if (byLabel) return byLabel;
  }

  const fromApi = normalizeApiIconToken(iconFromApi);
  if (fromApi) return fromApi;

  return iconFromApi?.trim() || 'Folder';
}

export function applyVaultMenuIcons(items: VaultSidebarMenuItem[]): VaultSidebarMenuItem[] {
  return items.map((item) => {
    const optionKey = (item as VaultSidebarMenuItem & { optionKey?: string }).optionKey;
    const icon = resolveVaultMenuIcon(item.icon, item.label, optionKey);
    return {
      ...item,
      icon,
      children: item.children?.length ? applyVaultMenuIcons(item.children) : item.children,
    };
  });
}
